import React, { cloneElement, useCallback, useRef, useState } from 'react';
import usePopper from 'use-popper';
import Portal from './Portal';
import { node, oneOf, oneOfType, string } from 'prop-types';
import { findDOMNode } from 'react-dom';
import useMergedRefs from './hooks';
import OverlayTarget from './OverlayTarget';
/**
 * OverlayTrigger Component
 * @typedef {'top'|'top-start'|'top-end'|'bottom'|'bottom-start'|'bottom-end'|'left'|'left-start'|'left-end'|'right'|'right-start'|'right-end'} PlacementProps
 * @typedef {{
 * placement: PlacementProps,
 * overlay: React.ReactNode,
 * trigger: 'hover' | 'click'
 * id: string
 * }} OverlaytriggerProps
 * @type {React.FC<OverlaytriggerProps>}
 * @returns
 */
const OverlayTrigger = (props) => {
  const { placement, trigger, overlay, id, children } = props
  const enterToRef = useRef('');
  const [show, setShow] = useState(false);
  const { reference, popper, arrow } = usePopper({
    placement,
    modifiers: {
      offset: { enabled: true, offset: '0 6px' },
    },
  });

  const mergedRef = useMergedRefs(reference.ref, children?.ref);

  // check DOM Node, it's component or HTML element
  const safeFindDOMNode = componentOrElement => {
    if (componentOrElement && 'setState' in componentOrElement) {
      return findDOMNode(componentOrElement);
    }
    return componentOrElement ?? null;
  };

  const { onClick } =
    typeof children !== 'function' ? React.Children.only(children).props : {};

  /**
   * Show Overlay
   */
  const handleShow = () => {
    clearTimeout(enterToRef.current);
    enterToRef.current = setTimeout(() => {
      setShow(true);
    }, 200);
  };

  /**
   * Hide Overlay
   */
  const handleHide = () => {
    clearTimeout(enterToRef.current);
    setShow(false);
  };

  /**
   *  handle show overlay if trigger is 'click'
   */
  const handleClick = useCallback(
    (...args) => {
      setShow(!show);
      if (onClick) onClick(...args);
    },
    [onClick, setShow, show]
  );

  /**
   * function to merge children ref
   * @param {React.ComponentClass | Element | null} componentOrElement
   */
  const attachRef = componentOrElement => {
    mergedRef(safeFindDOMNode(componentOrElement));
  };

  const targetProps = {
    'aria-describedby': id,
    ref: attachRef,
    ...(trigger === 'click'
      ? { onClick: handleClick }
      : {
          onMouseOver: handleShow,
          onMouseOut: handleHide,
        }),
  };

  const popperProps = {
    id,
    popper,
    arrow,
    show,
  };

  if (!children) {
    return null;
  }

  return (
    <>
      {typeof children === 'function' ? (
        children({ ...targetProps, show })
      ) : (
        <OverlayTarget ref={attachRef}>
          {cloneElement(children, targetProps)}
        </OverlayTarget>
      )}
      {show && <Portal>{cloneElement(overlay, popperProps)}</Portal>}
    </>
  );
};

OverlayTrigger.defaultProps = {
  placement: 'top',
  trigger: 'hover',
};

OverlayTrigger.propTypes = {
  id: string,
  overlay: oneOfType([node, string]),
  trigger: oneOf(['click', 'hover']),
  placement: oneOf([
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
  ]),
};

export default OverlayTrigger;
